import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  AutoAwesomeRounded,
  BarChartRounded,
  DomainRounded,
  GroupsRounded,
  Inventory2Rounded,
  PeopleRounded,
  SettingsRounded,
  ShoppingCartRounded,
} from '@mui/icons-material';
import {
  auth,
  corePulse,
  dashFlow,
  floatPlain,
  floatY,
  moduleAccent,
  reducedMotion,
  spin,
  spinReverse,
  type ModuleAccent,
} from './authTokens';
import StratifyGlyph from './StratifyGlyph';

interface OrbitModule {
  label: string;
  caption: string;
  accent: ModuleAccent;
  Icon: React.ElementType;
}

/**
 * The eight platform pillars, in clockwise orbit order starting at 12 o'clock.
 * Order is chosen so adjacent tiles never share an accent hue.
 */
const MODULES: OrbitModule[] = [
  { label: 'Finance', caption: 'Drive profitability', accent: 'blue', Icon: BarChartRounded },
  { label: 'Supply Chain', caption: 'Move the world', accent: 'green', Icon: Inventory2Rounded },
  { label: 'Operations', caption: 'Run efficiently', accent: 'slate', Icon: SettingsRounded },
  { label: 'Assets & Projects', caption: 'Build for tomorrow', accent: 'amber', Icon: DomainRounded },
  { label: 'AI & Analytics', caption: "See what's next", accent: 'violet', Icon: AutoAwesomeRounded },
  { label: 'Procurement', caption: 'Source smarter', accent: 'teal', Icon: ShoppingCartRounded },
  { label: 'HR & People', caption: 'Empower teams', accent: 'orange', Icon: PeopleRounded },
  { label: 'Sales & CRM', caption: 'Build relationships', accent: 'indigo', Icon: GroupsRounded },
];

/**
 * Orbit geometry, in percent of the container box.
 *
 * The radii are deliberately smaller than they look: a tile is ~150px wide and
 * is centred on its point, so at rx=37 the outermost tile edge lands at roughly
 * 87% + 75px, which still clears the container on the narrowest desktop column
 * this renders in.
 */
const RX = 37;
const RY = 33;

const positions = MODULES.map((_, i) => {
  const angle = (-90 + i * 45) * (Math.PI / 180);
  return {
    x: 50 + RX * Math.cos(angle),
    y: 50 + RY * Math.sin(angle),
    // Tiles on the left hemisphere mirror (text first, icon outboard) so every
    // label reads away from the core instead of into it.
    mirrored: Math.cos(angle) < -0.01,
  };
});

/** The icon tile, shared by the orbit and the mobile grid fallback. */
const IconTile: React.FC<{ accent: string; Icon: React.ElementType; size?: number }> = ({
  accent,
  Icon,
  size = 40,
}) => (
  <Box
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: `${size * 0.3}px`,
      display: 'grid',
      placeItems: 'center',
      // A tinted fill plus a same-hue ring: at this size a solid fill blows out
      // the glyph, and a bare outline disappears against the ink field.
      backgroundColor: `${accent}22`,
      border: `1px solid ${accent}55`,
      boxShadow: `0 6px 18px ${accent}2e, inset 0 1px 0 rgba(255,255,255,0.10)`,
      transition: 'box-shadow .3s ease, background-color .3s ease',
    }}
  >
    <Box component={Icon} sx={{ fontSize: size * 0.5, color: accent }} />
  </Box>
);

/**
 * The hero visual: eight module tiles in a slow elliptical orbit around a
 * glowing Stratify core, wired back to it with flowing connectors.
 *
 * Built entirely from CSS gradients and inline SVG rather than an image, so it
 * stays crisp at any size, themes with the brand, and costs no extra request
 * on the one screen where time-to-interactive matters most.
 *
 * Below `md` the orbit is dropped for a plain two-column grid: at phone width
 * the ellipse would either overlap its own tiles or shrink the labels below a
 * readable size, and neither is worth the decoration.
 */
const ModuleOrbit: React.FC = () => {
  // Connector endpoints stop short of each tile so the line meets the tile edge
  // rather than driving through its middle.
  const connectors = useMemo(
    () => positions.map((p) => ({ x: 50 + (p.x - 50) * 0.74, y: 50 + (p.y - 50) * 0.74 })),
    []
  );

  return (
    <>
      {/* ---------- Desktop: the orbit ---------- */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'relative',
          width: '100%',
          maxWidth: 600,
          aspectRatio: '1 / 0.95',
          mx: 'auto',
        }}
      >
        {/* Orbit rings. Two counter-rotating ellipse pairs read as depth; one
            alone reads as a spinning wheel. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            animation: `${spin} 70s linear infinite`,
            ...reducedMotion,
          }}
        >
          <Box component="svg" viewBox="0 0 100 100" sx={{ width: '100%', height: '100%' }} preserveAspectRatio="none">
            <ellipse cx="50" cy="50" rx={RX} ry={RY} fill="none" stroke="rgba(143,170,240,0.16)" strokeWidth="0.22" />
            <ellipse cx="50" cy="50" rx={RX - 9} ry={RY - 8} fill="none" stroke="rgba(143,170,240,0.10)" strokeWidth="0.2" />
          </Box>
        </Box>
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: '12%',
            animation: `${spinReverse} 52s linear infinite`,
            ...reducedMotion,
          }}
        >
          <Box component="svg" viewBox="0 0 100 100" sx={{ width: '100%', height: '100%' }} preserveAspectRatio="none">
            <ellipse
              cx="50"
              cy="50"
              rx="44"
              ry="30"
              fill="none"
              stroke="rgba(139,92,246,0.22)"
              strokeWidth="0.3"
              strokeDasharray="2 3"
            />
          </Box>
        </Box>

        {/* Connectors — core to tile, with a flowing dash. */}
        <Box
          component="svg"
          aria-hidden
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        >
          {connectors.map((c, i) => (
            <g key={MODULES[i].label}>
              {/* Static base line keeps the wire visible when the dash is mid-gap. */}
              <line x1="50" y1="50" x2={c.x} y2={c.y} stroke="rgba(150,175,235,0.13)" strokeWidth="0.18" />
              <Box
                component="line"
                x1="50"
                y1="50"
                x2={c.x}
                y2={c.y}
                stroke={moduleAccent[MODULES[i].accent]}
                strokeWidth="0.28"
                strokeDasharray="2 6"
                sx={{
                  opacity: 0.6,
                  animation: `${dashFlow} ${3.2 + i * 0.25}s linear infinite`,
                  ...reducedMotion,
                }}
              />
            </g>
          ))}
        </Box>

        {/* Travelling data dots. Positioned rather than transformed because a
            percentage translate resolves against the dot's own 6px box, not the
            container, which would make the path meaningless. */}
        {positions.map((p, i) => {
          const travel = `orbitTravel${i}`;
          return (
            <Box
              key={`dot-${MODULES[i].label}`}
              aria-hidden
              sx={{
                position: 'absolute',
                width: 5,
                height: 5,
                borderRadius: '50%',
                marginLeft: '-2.5px',
                marginTop: '-2.5px',
                backgroundColor: moduleAccent[MODULES[i].accent],
                boxShadow: `0 0 8px ${moduleAccent[MODULES[i].accent]}`,
                [`@keyframes ${travel}`]: {
                  '0%': { left: '50%', top: '50%', opacity: 0 },
                  '15%': { opacity: 1 },
                  '80%': { opacity: 1 },
                  '100%': { left: `${p.x}%`, top: `${p.y}%`, opacity: 0 },
                },
                left: '50%',
                top: '50%',
                animation: `${travel} ${4 + (i % 3)}s ease-in-out ${i * 0.55}s infinite`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
              }}
            />
          );
        })}

        {/* The core. */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 148,
            height: 148,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {/* Outer bloom, behind everything, no pointer surface. */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: '-52%',
              borderRadius: '50%',
              background:
                'radial-gradient(circle at 50% 50%, rgba(124,92,245,0.34) 0%, rgba(79,127,245,0.16) 42%, rgba(79,127,245,0) 70%)',
              animation: `${corePulse} 6s ease-in-out infinite`,
              ...reducedMotion,
            }}
          />
          {/* The sphere itself: an off-centre highlight is what separates a
              sphere from a flat disc. */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background:
                'radial-gradient(circle at 34% 28%, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0) 38%),' +
                'radial-gradient(circle at 50% 52%, #6f63f3 0%, #3f56d4 46%, #17204a 100%)',
              border: '1px solid rgba(255,255,255,0.16)',
              boxShadow: '0 20px 60px rgba(63,86,212,0.45), inset 0 -18px 40px rgba(4,8,26,0.6)',
            }}
          />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <StratifyGlyph size={54} flat />
          </Box>
        </Box>

        {/* Module tiles. */}
        {MODULES.map((m, i) => {
          const p = positions[i];
          const accent = moduleAccent[m.accent];
          return (
            <Box
              key={m.label}
              sx={{
                position: 'absolute',
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: 152,
                display: 'flex',
                flexDirection: p.mirrored ? 'row-reverse' : 'row',
                alignItems: 'center',
                gap: 1.25,
                textAlign: p.mirrored ? 'right' : 'left',
                // The float keyframes re-declare the centring translate, because
                // a transform animation replaces the static transform outright.
                transform: 'translate(-50%, -50%)',
                animation: `${floatY} ${5.5 + (i % 4) * 0.7}s ease-in-out ${i * 0.32}s infinite`,
                transition: 'filter .3s ease',
                '&:hover': { filter: 'brightness(1.18)' },
                ...reducedMotion,
              }}
            >
              <IconTile accent={accent} Icon={m.Icon} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    lineHeight: 1.25,
                    color: auth.text,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {m.label}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', lineHeight: 1.3, color: auth.textFaint, whiteSpace: 'nowrap' }}>
                  {m.caption}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ---------- Mobile: plain grid ---------- */}
      <Box
        sx={{
          display: { xs: 'grid', md: 'none' },
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 1.5,
        }}
      >
        {MODULES.map((m, i) => {
          const accent = moduleAccent[m.accent];
          return (
            <Box
              key={m.label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 1.25,
                minWidth: 0,
                borderRadius: '14px',
                border: `1px solid ${auth.hairline}`,
                backgroundColor: 'rgba(255,255,255,0.035)',
                animation: `${floatPlain} ${6 + (i % 3)}s ease-in-out ${i * 0.3}s infinite`,
                ...reducedMotion,
              }}
            >
              <IconTile accent={accent} Icon={m.Icon} size={34} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    color: auth.text,
                    lineHeight: 1.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {m.label}
                </Typography>
                <Typography sx={{ fontSize: '0.6875rem', color: auth.textFaint, lineHeight: 1.3 }}>
                  {m.caption}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </>
  );
};

export default ModuleOrbit;
